import { useMemo } from 'react';
import { Col, Row, Card, Button } from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { validate as uuidValidate } from 'uuid';

import LinkContainer from '../components/link_container';
import { renderOverlay, renderOffline, renderError } from '../components/render_helpers';
import ReactTable from '../components/react_table/react_table';
import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import { settingsSet } from '../store/settings_slice';
import {
  inverterListPageQuery,
  inverterDelete as inverterDeleteMutation,
} from '../graphql/inverter_queries';
import * as updateFunctions from '../update_functions';

const InverterList = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const [inverterDelete] = useMutation(inverterDeleteMutation);
  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    refetch: pageRefetch,
    networkStatus: pageNetworkStatus,
    client: pageClient,
  } = useQuery(inverterListPageQuery, {
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ inverterList }) => {
      // write an individual query for every inverter in the list
      updateFunctions.inverterList(pageClient, inverterList);
    },
  });
  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const inverterDeleteClicked = async (e) => {
    const dataId = e.currentTarget.getAttribute('data-id');
    const inverterId = uuidValidate(dataId)
      ? dataId
      : parseInt(e.currentTarget.getAttribute('data-id'), 10);
    const mutationData = {
      variables: { id: inverterId },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'InverterType',
        recordId: inverterId,
        mutationType: 'DELETE',
      },
      update: updateFunctions.inverterDelete,
    };
    mutationData.optimisticResponse = updateFunctions.optimistic(
      'inverterDelete',
      mutationData
    );
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await inverterDelete(mutationData);
        toastSuccess('Inverter delete ok');
      } catch (err) {
        console.log(err.toString());
        toastError('Inverter delete failed');
      } finally {
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
      }
    } else {
      inverterDelete(mutationData);
      toastWarning(`Inverter delete ok locally. Go online to make permanent`);
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
    }
  };

  const getSiteName = (row) => {
    const {
      site: { name: siteName },
    } = row;
    return siteName;
  };

  const parentColumns = [
    {
      header: 'Site',
      id: 'site',
      accessorFn: (row) => getSiteName(row),
      filterType: 'dropdown',
    },
    {
      header: 'Name',
      accessorKey: 'name',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
  ];

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col sm="auto">
          <h1 className="h3 mb-3">Inverters</h1>
        </Col>
        <Col>
          <Row className="justify-content-end g-0">
            <Col sm="auto">
              <Button
                className="me-2"
                variant="primary"
                onClick={() => pageRefetch()}
                disabled={!settingsOnline}
              >
                Refresh
              </Button>
              <LinkContainer to="/inverters/new">
                <Button variant="primary">New Inverter</Button>
              </LinkContainer>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <ReactTable
                rootName="inverter"
                parentColumns={parentColumns}
                data={pageData.inverterList}
                doShow={false}
                doDelete={currentUser?.perms?.administrator}
                handleDelete={inverterDeleteClicked}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default InverterList;
